import { openDialog } from '@patrianna/dialog-manager'
import { nextTick } from '@patrianna/shared-utils'
import cx from 'classnames'
import { usePathname, useRouter } from 'next/navigation'

import ProductTile from 'components/ProductTile'
import productTileClasses from 'components/ProductTile/styles.module.scss'
import BingoJackpotTicker from 'containers/Bingo/BingoTile/components/BingoJackpotTicker'
import { useBingoJackpotTicker } from 'src/hooks/useBingoJackpotTicker'
import { useHorizontalPageIndex } from 'src/hooks/useHorizontalPageIndex'
import { useIsGameGeoBlocked } from 'src/hooks/useIsGameGeoBlocked'
import { useHasJackpotTicker } from 'src/hooks/useJackpotTickerData'
import { useVerticalPageIndex } from 'src/hooks/useVerticalPageIndex'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { getActiveCurrencyIdSelector } from 'store/modules/currencies/selectors'
import { startExternalGame } from 'store/modules/slotGameFlow/actions'

import type { GameProductShort } from '@patrianna/shared-patrianna-types/store/GameModule'
import type { MouseEvent } from 'react'

type Props = {
  game: GameProductShort
  onClick?: () => void
  className?: string
  category?: string
  gameIndex?: number
  swimlaneIndex?: number
  with3DImage?: boolean
  showFavorite?: boolean
  refererPath?: string
  layout?: string
}

export default function ProductGameTile(props: Props) {
  const { category, gameIndex, swimlaneIndex, refererPath, layout } = props
  const router = useRouter()
  const pathname = usePathname()
  const dispatch = useAppDispatch()
  const activeCurrency = useAppSelector(getActiveCurrencyIdSelector)
  const getVerticalPageIndex = useVerticalPageIndex()
  const getHorizontalPageIndex = useHorizontalPageIndex(category, false, pathname)
  const xPageIndex = getHorizontalPageIndex(props?.gameIndex)
  const hasJackpotTicker = useHasJackpotTicker(props.game)
  const { hasBingoJackpot, bingoJackpotAmount, bingoJackpotCurrency } = useBingoJackpotTicker(props.game)
  // PLDV-8952: every lobby surface renders through this tile, so hiding here covers
  // swimlanes, search, favorites, previously played and Builder game blocks at once.
  const isGeoBlocked = useIsGameGeoBlocked(props.game.code)

  if (isGeoBlocked) {
    return null
  }

  const bingoJackpotElement = hasBingoJackpot ? (
    <BingoJackpotTicker
      key='jackpot-ticker'
      amount={bingoJackpotAmount!}
      currency={bingoJackpotCurrency!}
      className={cx(productTileClasses.tagItem, productTileClasses.tagJackpot)}
    />
  ) : undefined

  const onTileClick = (event: MouseEvent<HTMLDivElement>) => {
    nextTick().then(() => {
      if (props.game.type === 'bingo') {
        dispatch(
          startExternalGame(props.game, router.push, {
            gameIndex,
            category,
            refererPath: refererPath || pathname,
            xPageIndex,
            yPageIndex: getVerticalPageIndex(event.clientY),
            layout,
          }),
        )
      } else {
        openDialog({
          dialogName: 'START_GAME_FLOW',
          dialogProps: {
            game: props.game,
            gameIndex,
            category,
            swimlaneIndex,
            refererPath,
            gameWith3DImage: props?.with3DImage,
            verticalPageIndex: getVerticalPageIndex(event.clientY),
            layout,
          },
        })
      }
    })
  }

  return (
    <ProductTile
      with3DImage={props?.with3DImage}
      showFavorite={props.showFavorite}
      className={props.className}
      role='link'
      onClick={onTileClick}
      game={props.game}
      activeCurrency={activeCurrency}
      hasJackpotTicker={hasJackpotTicker || hasBingoJackpot}
      jackpotElement={bingoJackpotElement}
    />
  )
}
